import Axios, { type AxiosRequestHeaders, type AxiosInstance, type AxiosPromise } from 'axios';
import {
	type AemBookingDay,
	type ITimeSlot,
	type ITourFormData
} from 'Types/aemui/schedule-a-tour/scheduleATour';
import {
	type IBookingsService,
	type IBookingsServiceResponse,
	type IBookingsStaffAvailabilityResponse,
	BookingAppointmentRequest
} from 'Types/bookings/bookings';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { bookingsTokenService } from './bookingsTokenService';
import { club } from '@/util/club';
import { logError } from '@/util/logger';
import { getAvailableTimeSlots } from '@/util/timeSlotAvailability';

dayjs.extend(duration);

export const BookingsServiceAxiosInstance: AxiosInstance = Axios.create({
	baseURL: 'https://graph.microsoft.com/v1.0/solutions/bookingBusinesses'
});

class BookingsService {
	axiosInstance: AxiosInstance = BookingsServiceAxiosInstance;
	authHeader: null | AxiosRequestHeaders = null;

	private getBookingsBusiness() {
		// Use QA Club for any other env than prod
		return window.location.hostname === 'www.lifetime.life'
			? window.lt.club.smtpScheduleTour
			: 'LifeTimeChanhassenQA@ltfinc.onmicrosoft.com';
	}

	private async getAuthHeader(): Promise<AxiosRequestHeaders> {
		if (this.authHeader) {
			return this.authHeader;
		}
		try {
			const token = await bookingsTokenService.getToken();
			this.authHeader = {
				Authorization: `Bearer ${token.data.access_token}`
			};
			return this.authHeader;
		} catch (e: unknown) {
			logError('Error getting bookings token', {}, e);
		}
		return {};
	}

	async getClubTourService(): AxiosPromise<IBookingsServiceResponse> {
		const bookingsBusiness = this.getBookingsBusiness();
		const headers = await this.getAuthHeader();
		const params = new URLSearchParams();
		params.append('$filter', "displayName eq 'Club Tour'");

		return this.axiosInstance.get(`/${bookingsBusiness}/services`, {
			headers,
			params
		});
	}

	async getStaffAvailability(
		service: IBookingsService
	): AxiosPromise<IBookingsStaffAvailabilityResponse> {
		const startDate = dayjs()
			.set('hour', 0)
			.set('minute', 0)
			.set('second', 0)
			.set('millisecond', 0);
		const maximumAdvance = dayjs.duration(service.schedulingPolicy.maximumAdvance).asDays();
		const endDate = startDate.add(maximumAdvance, 'day').endOf('day');

		const request = {
			staffIds: service.staffMemberIds,
			startDateTime: {
				dateTime: startDate,
				timeZone: 'UTC'
			},
			endDateTime: {
				dateTime: endDate,
				timeZone: 'UTC'
			}
		};

		const bookingsBusiness = this.getBookingsBusiness();
		const headers = await this.getAuthHeader();
		return this.axiosInstance.post(`/${bookingsBusiness}/getStaffAvailability`, request, {
			headers
		});
	}

	async getTourDays(): Promise<AemBookingDay[]> {
		try {
			const bookingsBusiness = this.getBookingsBusiness();
			const services = await this.getClubTourService();

			// Make call to staff availability and process it.
			const availability = await this.getStaffAvailability(services.data.value[0]!);

			const days = getAvailableTimeSlots(
				services.data.value[0]!,
				availability.data.value[0]!,
				club.timeZone,
				bookingsBusiness
			);

			return days;
		} catch (error: unknown) {
			if (Axios.isAxiosError(error)) {
				throw error;
			}
			throw new Error('An unknown error was thrown');
		}
	}

	async submitBooking(timeSlot: ITimeSlot, formData: ITourFormData): AxiosPromise {
		let days: AemBookingDay[];
		try {
			days = await this.getTourDays();
		} catch (error: unknown) {
			if (Axios.isAxiosError(error)) {
				throw error;
			}
			throw new Error('Error retrieving current time slots');
		}

		const isStillAvailable = days.some((x) =>
			x.timeSlots.some((y) => dayjs(y.startTime).isSame(timeSlot.startTime, 'day'))
		);

		if (isStillAvailable) {
			const headers = await this.getAuthHeader();
			return this.axiosInstance.post(
				`/${timeSlot.bookingsBusiness}/appointments`,
				new BookingAppointmentRequest(timeSlot, formData),
				{ headers }
			);
		} else {
			throw new Error('Staff not available:');
		}
	}
}

export const bookingsService = new BookingsService();
